import {
  AloftDate,
  AloftPrice,
  PairedCostRouteItemWithQuery,
  orUndefined,
  schAlertType,
  schAloftPrice,
  schPairedCostRouteItem,
  syntaxFNMA,
} from '@aloft/toolkit-shared';

import { CompPropertyDataState } from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';
import { getComparableDisplayName, getScopedAlertType } from '../utils/compTypeScoping.ts';

const THRESHOLD_MONTHS = 12;
const THRESHOLD_PRICE_DIFF = 0.3;

export const similarComparableToSubjectPriorSale: AlertDefinition = ({
  compSequence,
  compType,
  priorSales,
  propertyDataEntities,
}) => {
  const subjectPriorSale = priorSales.subject?.[0];

  if (!subjectPriorSale) return undefined;

  const priorSaleDate = orUndefined(AloftDate.fromIsoString)(subjectPriorSale.CloseDate);
  const priorSalePrice = orUndefined(AloftPrice.fromDollarLike)(subjectPriorSale.ClosePrice);
  if (!priorSaleDate || !priorSalePrice) return undefined;

  const faultyComp = compSequence
    .map((id, index) => {
      const propertyData = (propertyDataEntities[id] as CompPropertyDataState | undefined)
        ?.compData;

      const saleDate = orUndefined(syntaxFNMA.isoMonthToAloftDate)(
        propertyData?.editableData.transaction.saleDate,
      );
      const salePrice = orUndefined(schAloftPrice.parse)(
        propertyData?.editableData.transaction.price,
      );

      return { id, index, saleDate, salePrice };
    })
    .filter(
      (c): c is { id: string; index: number; saleDate: AloftDate; salePrice: AloftPrice } =>
        !!c.saleDate && !!c.salePrice,
    )
    .find(({ saleDate, salePrice }) => {
      const differenceInMonths = Math.abs(priorSaleDate.differenceInCalendarMonths(saleDate));
      if (differenceInMonths > THRESHOLD_MONTHS) return false;

      const priceDifference = salePrice.sub(priorSalePrice).abs();

      return priceDifference.ratio(priorSalePrice) > THRESHOLD_PRICE_DIFF;
    });

  if (!faultyComp) return undefined;

  const { id: propertyID, saleDate, salePrice } = faultyComp;
  const propertyName = getComparableDisplayName(propertyID, compSequence);

  const relationship = salePrice.gt(priorSalePrice) ? 'higher' : 'lower';

  return {
    type: getScopedAlertType(schAlertType.enum.similarComparableToSubjectPriorSale, compType),
    status: 'active',
    severity: 'error',
    orderType: ['appraisify'],
    message: `${propertyName} was sold (${saleDate.toString()}) within 12 months of the Subject's prior transaction (${priorSaleDate.toString()}) at a significantly ${relationship} price.`,
    propertyID,
    ...(compType ? { compType } : {}),
    fixingInfo: {
      tips: 'Tips: Review the historical market relationship between the properties, and determine if the same relative positioning is still supported by current market evidence. The prior sales price relationship should remain consistent as of the Effective Date unless the Subject or the noted Comparable have changed significantly since these prior transactions.',
      pairedCostRouteItem:
        `${schPairedCostRouteItem.enum['appraisify-commentary']}#prior-sales` as PairedCostRouteItemWithQuery,
      fixingMethod: ['comment', 'action'],
      commentAlwaysRequired: false,
    },
    triggers: [],
  };
};
